import { useEffect, useMemo, useState } from 'react'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import Swal from 'sweetalert2'
import moment from 'moment'
import './MyReturns.scss'

import { useCustomReducer } from '../../../../../base/customHooks'
import IPModal from '../../../../../components/IPModal'
import IPButton from '../../../../../components/IPButton'
import IPSelectBox from '../../../../../components/IPSelectBox'
import IPInput from '../../../../../components/IPInput'
import InputCampaign from '../../../../../components/InputCampaign'
import Table from '../../../../../components/Table'

import { getDateByTimezoneOffset } from '../../../../../base/utils/dataHelper'
import { IBrand } from '../../OrderOperation/models/responseModels'
import { ISelectOption } from '../../../HomeAndSiteOperation/BulkAdding/components/BulkAdding'
import { getBrandApi, getCompanyApi } from '../../../components/api'

import { GetOrderReportApi } from '../services/getOrderReportApi'
import { GetOrderReportUsersApi } from '../services/getOrderReportUsersApi'
import { GetOrderReportByMtApi } from '../services/getOrderReportByMtApi'

// New summary apis
import { GetOrderReportByProductApi } from '../services/getOrderReportByProductApi'
import { GetOrderReportByDealerApi } from '../services/getOrderReportByDealerApi'
import { GetOrderReportByReasonApi } from '../services/getOrderReportByReasonApi'

import {
  IReportRequest,
  IReportResponse,
  IReturnSummaryByMT,
  IReturnSummaryByProduct,
  IReturnSummaryByDealer,
  IReturnSummaryByReason,
} from '../models/models'
import { Link } from 'react-router-dom'
import { ICompany } from '../../../components/models/models'

interface IReturnReportState {
  companies: ISelectOption[]
  selectedCompany?: ISelectOption
  selectedType?: ISelectOption
  selectedStatus?: ISelectOption
  dealerCode?: string
  productCode?: string
  brandList?: IBrand[]
  personelList?: any[]
  brand?: ISelectOption
  personel?: ISelectOption
  reason?: ISelectOption
}

function Index(props: BaseComponentWithContextProps) {
  const getInitialState = (): IReturnReportState => ({
    companies: [],
    selectedCompany: undefined,
  })

  const [state, setState] = useCustomReducer<IReturnReportState>(getInitialState())

  // Detail + MT
  const [returnList, setReturnList] = useState<IReportResponse[]>([])
  const [returnMTList, setReturnMTList] = useState<IReturnSummaryByMT[]>([])

  // Product/Dealer/Reason summaries
  const [returnProductList, setReturnProductList] = useState<IReturnSummaryByProduct[]>([])
  const [returnDealerList, setReturnDealerList] = useState<IReturnSummaryByDealer[]>([])
  const [returnReasonList, setReturnReasonList] = useState<IReturnSummaryByReason[]>([])

  // -------------------------
  // MT Modal state
  // -------------------------
  const [isMTModalOpen, setIsMTModalOpen] = useState(false)
  const [selectedMT, setSelectedMT] = useState<IReturnSummaryByMT | null>(null)
  const [selectedMTReasonId, setSelectedMTReasonId] = useState<number | null>(null)

  // -------------------------
  // Product Modal state
  // -------------------------
  const [isProductModalOpen, setIsProductModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<IReturnSummaryByProduct | null>(null)
  const [selectedProductReasonId, setSelectedProductReasonId] = useState<number | null>(null)

  // -------------------------
  // Dealer Modal state
  // -------------------------
  const [isDealerModalOpen, setIsDealerModalOpen] = useState(false)
  const [selectedDealer, setSelectedDealer] = useState<IReturnSummaryByDealer | null>(null)
  const [selectedDealerReasonId, setSelectedDealerReasonId] = useState<number | null>(null)

  // -------------------------
  // Reason Modal state
  // -------------------------
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false)
  const [selectedReasonSummary, setSelectedReasonSummary] = useState<IReturnSummaryByReason | null>(null)

  // Dates
  const inititalStartDate = new Date()
  inititalStartDate.setDate(1)
  inititalStartDate.setHours(0, 0, 0, 0)

  const [startDate, setStartDate] = useState<Date | any>(inititalStartDate)
  const [endDate, setEndDate] = useState<Date | any>(new Date())

  const types = [
    { label: 'İptal', value: 0 },
    { label: 'İade', value: 1 },
  ]

  const status = [
    { label: 'Onaylananlar', value: 2 },
    { label: 'Reddedilenler', value: 1 },
    { label: 'Silinenler', value: 3 },
    { label: 'İşlem Görmemişler', value: 0 },
  ]

  const iadeNedenleri = [
    { label: 'Depoda Bekleyen Ürün', value: 0 },
    { label: 'Ayıplı Ürün', value: 1 },
    { label: 'Yanlış Ürün', value: 2 },
    { label: 'Yanlış Sipariş Verilmesi', value: 3 },
    { label: 'Siparişin Yanlış Çıkarılması', value: 4 },
    { label: 'Siparişten Vazgeçilmesi', value: 5 },
    { label: 'Miktar Hatası', value: 6 },
    { label: 'Uyumsuz Ürün', value: 7 },
    { label: 'Yanlış Katalog Bilgisi', value: 8 },
    { label: 'Yanlış Teslimat Seçimi', value: 9 },
    { label: 'Diğer', value: 10 },
  ]

  const toDateTimeLocal = (date: any) => {
    if (date != null) {
      return date
        .toLocaleString('sv-SE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
        .replace(' ', 'T')
    }
    return undefined
  }

  const getCompanies = async () => {
    try {
      const companyList: ICompany[] = await getCompanyApi()
      const companiesOptions: ISelectOption[] = companyList.map(company => ({
        label: company.Name,
        value: company.Code.toString(),
      }))
      setState({ companies: companiesOptions })
    } catch (err) {
      console.log('Fetch Companies Error:', err)
    }
  }

  const getDate = async (data: any, title: 'Start' | 'End') => {
    if (title === 'Start') setStartDate(data)
    else setEndDate(data)
  }

  const buildRequest = (): IReportRequest => ({
    StartDate: startDate ? getDateByTimezoneOffset(startDate) : undefined,
    EndDate: endDate ? getDateByTimezoneOffset(endDate) : undefined,
    DealerCode: state.dealerCode?.length === 6 ? '0000' + state.dealerCode : state.dealerCode,
    ProductCode: state.productCode,
    Brand: state.brand?.label?.toString(),
    Reason: state.reason?.value != null ? Number(state.reason.value) : undefined,
    AppUser: state.personel?.value != null ? Number(state.personel.value) : undefined,
    Company: state.selectedCompany?.value != null ? String(state.selectedCompany.value) : undefined,
    Type: state.selectedType?.value != null ? Number(state.selectedType.value) : undefined,
    Status: state.selectedStatus?.value != null ? Number(state.selectedStatus.value) : undefined,
  })

  const showErr = (err: any) => {
    Swal.fire({
      icon: 'error',
      title: err?.description ?? 'Hata oluştu',
      showConfirmButton: true,
      allowOutsideClick: false,
    })
  }

  const getBrand = async () => {
    const data: any[] = await getBrandApi()
    setState({ brandList: data })
  }

  const getUsers = async () => {
    const usersapi = new GetOrderReportUsersApi({})
    const data: ISelectOption[] = await usersapi.getReport()
    setState({ personelList: data })
  }

  // -------------------------
  // Detail
  // -------------------------
  const getOrderReport = async () => {
    const req = buildRequest()
    props.showLoading()
    const api = new GetOrderReportApi({})
    api
      .getReport(req)
      .then((result: IReportResponse[]) => {
        setReturnList(result || [])
        props.hideLoading()
      })
      .catch((err) => {
        showErr(err)
        props.hideLoading()
      })
  }

  // -------------------------
  // MT Summary
  // -------------------------
  const getOrderReportByMT = async () => {
    const req = buildRequest()
    props.showLoading()
    const api = new GetOrderReportByMtApi({})
    api
      .getReport(req)
      .then((result: IReturnSummaryByMT[]) => {
        setReturnMTList(result || [])
        props.hideLoading()
      })
      .catch((err) => {
        showErr(err)
        props.hideLoading()
      })
  }

  // -------------------------
  // Product / Dealer / Reason summaries
  // -------------------------
  const getOrderReportByProduct = async () => {
    const req = buildRequest()
    props.showLoading()
    const api = new GetOrderReportByProductApi({})
    api
      .getReport(req)
      .then((result: IReturnSummaryByProduct[]) => {
        setReturnProductList(result || [])
        props.hideLoading()
      })
      .catch((err) => {
        showErr(err)
        props.hideLoading()
      })
  }

  const getOrderReportByDealer = async () => {
    const req = buildRequest()
    props.showLoading()
    const api = new GetOrderReportByDealerApi({})
    api
      .getReport(req)
      .then((result: IReturnSummaryByDealer[]) => {
        setReturnDealerList(result || [])
        props.hideLoading()
      })
      .catch((err) => {
        showErr(err)
        props.hideLoading()
      })
  }

  const getOrderReportByReason = async () => {
    const req = buildRequest()
    props.showLoading()
    const api = new GetOrderReportByReasonApi({})
    api
      .getReport(req)
      .then((result: IReturnSummaryByReason[]) => {
        setReturnReasonList(result || [])
        props.hideLoading()
      })
      .catch((err) => {
        showErr(err)
        props.hideLoading()
      })
  }

  const refreshAll = () => {
    getOrderReport()
    getOrderReportByMT()
    getOrderReportByProduct()
    getOrderReportByDealer()
    getOrderReportByReason()
  }

  useEffect(() => {
    refreshAll()
    getBrand()
    getUsers()
    getCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -------------------------
  // Detail Table
  // -------------------------
  const detailColumns = useMemo(
    () => [
      { Header: 'Kullanıcı', accessor: 'userId' },
      { Header: 'Bayi', accessor: 'dealer' },
      { Header: 'Bayi Adı', accessor: 'dealerName' },
      { Header: 'Sipariş', accessor: 'order' },
      { Header: 'Durum', accessor: 'status' },
      { Header: 'Red Nedeni', accessor: 'cancelReason' },
      { Header: 'Form No', accessor: 'form' },
      { Header: 'İşlem', accessor: 'type' },
      { Header: 'Ürün Kodu', accessor: 'material' },
      { Header: 'Ürün Adı', accessor: 'materialName' },
      { Header: 'Adet', accessor: 'amount' },
      { Header: 'Şirket', accessor: 'company' },
      { Header: 'Marka', accessor: 'brand' },
      { Header: 'Açıklama', accessor: 'text' },
      { Header: 'İade Nedeni', accessor: 'returnReason' },
      { Header: 'Personel', accessor: 'mtInfo' },
      { Header: 'Tarih', accessor: 'date' },
    ],
    []
  )

  const detailData = useMemo(
    () =>
      (returnList || []).map((item) => ({
        userId: item.Kullanici,
        dealer: item.BayiKodu,
        dealerName: item.BayiAdi,
        order: item.SiparisNo,
        status: item.Durum,
        cancelReason: item.RedNedeni,
        form: item.FormNo,
        type: item.İslemTipi,
        material: (
          <Link target="_blank" to={`/product/detail/${item.ProductId}?from=productList`}>
            {item.MalzemeNo}
          </Link>
        ),
        materialName: item.MalzemeAdi,
        amount: item.Miktar,
        company: item.Company,
        brand: item.Brand,
        text: item.Aciklama,
        returnReason: item.IadeNedeni,
        mtInfo: `${item.Name} ${item.Surname} (${item.UserName})`,
        date: item.Tarih ? moment(item.Tarih).format('DD.MM.YYYY') : '',
      })),
    [returnList]
  )

  // -------------------------
  // Open modals
  // -------------------------
  const openMTModal = (mt: IReturnSummaryByMT) => {
    setSelectedMT(mt)
    setSelectedMTReasonId(null)
    setIsMTModalOpen(true)
  }

  const openProductModal = (p: IReturnSummaryByProduct) => {
    setSelectedProduct(p)
    setSelectedProductReasonId(null)
    setIsProductModalOpen(true)
  }

  const openDealerModal = (d: IReturnSummaryByDealer) => {
    setSelectedDealer(d)
    setSelectedDealerReasonId(null)
    setIsDealerModalOpen(true)
  }

  const openReasonModal = (r: IReturnSummaryByReason) => {
    setSelectedReasonSummary(r)
    setIsReasonModalOpen(true)
  }

  // -------------------------
  // MT Summary Table
  // -------------------------
  const mtSummaryColumns = useMemo(
    () => [
      { Header: 'Personel', accessor: 'mt' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
      { Header: 'İlk Tarih', accessor: 'IlkTarih' },
      { Header: 'Son Tarih', accessor: 'SonTarih' },
      { Header: 'Detay', accessor: 'actions' },
    ],
    []
  )

  const mtSummaryData = useMemo(
    () =>
      (returnMTList || []).map((mt) => ({
        mt: `${mt.Name} ${mt.Surname} (${mt.UserName})`,
        KayitSayisi: mt.KayitSayisi,
        ToplamMiktar: mt.ToplamMiktar,
        Onayli: mt.Onayli,
        Reddedilen: mt.Reddedilen,
        IslemGormemis: mt.IslemGormemis,
        Silinen: mt.Silinen,
        Iptal: mt.Iptal,
        Iade: mt.Iade,
        IlkTarih: mt.IlkTarih ? moment(mt.IlkTarih).format('DD.MM.YYYY') : '',
        SonTarih: mt.SonTarih ? moment(mt.SonTarih).format('DD.MM.YYYY') : '',
        actions: (
          <div className="d-flex gap-2">
            <IPButton text="Detay" className="btn btn-sm btn-primary" onClick={() => openMTModal(mt)} />
          </div>
        ),
      })),
    [returnMTList]
  )

  // -------------------------
  // Product Summary Table (+ Detay)
  // -------------------------
  const productSummaryColumns = useMemo(
    () => [
      { Header: 'Ürün', accessor: 'product' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
      { Header: 'İlk Tarih', accessor: 'IlkTarih' },
      { Header: 'Son Tarih', accessor: 'SonTarih' },
      { Header: 'Detay', accessor: 'actions' },
    ],
    []
  )

  const productSummaryData = useMemo(
    () =>
      (returnProductList || []).map((p) => ({
        product: (
          <Link target="_blank" to={`/product/detail/${p.UrunId}?from=productList`}>
            {p.UrunKodu} - {p.UrunAdi}
          </Link>
        ),
        KayitSayisi: p.KayitSayisi,
        ToplamMiktar: p.ToplamMiktar,
        Onayli: p.Onayli,
        Reddedilen: p.Reddedilen,
        IslemGormemis: p.IslemGormemis,
        Silinen: p.Silinen,
        Iptal: p.Iptal,
        Iade: p.Iade,
        IlkTarih: p.IlkTarih ? moment(p.IlkTarih).format('DD.MM.YYYY') : '',
        SonTarih: p.SonTarih ? moment(p.SonTarih).format('DD.MM.YYYY') : '',
        actions: (
          <div className="d-flex gap-2">
            <IPButton text="Detay" className="btn btn-sm btn-primary" onClick={() => openProductModal(p)} />
          </div>
        ),
      })),
    [returnProductList]
  )

  // -------------------------
  // Dealer Summary Table (+ Detay)
  // -------------------------
  const dealerSummaryColumns = useMemo(
    () => [
      { Header: 'Bayi', accessor: 'dealer' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
      { Header: 'İlk Tarih', accessor: 'IlkTarih' },
      { Header: 'Son Tarih', accessor: 'SonTarih' },
      { Header: 'Detay', accessor: 'actions' },
    ],
    []
  )

  const dealerSummaryData = useMemo(
    () =>
      (returnDealerList || []).map((d) => ({
        dealer: d.DealerName ? `${d.DealerCode} - ${d.DealerName}` : d.DealerCode,
        KayitSayisi: d.KayitSayisi,
        ToplamMiktar: d.ToplamMiktar,
        Onayli: d.Onayli,
        Reddedilen: d.Reddedilen,
        IslemGormemis: d.IslemGormemis,
        Silinen: d.Silinen,
        Iptal: d.Iptal,
        Iade: d.Iade,
        IlkTarih: d.IlkTarih ? moment(d.IlkTarih).format('DD.MM.YYYY') : '',
        SonTarih: d.SonTarih ? moment(d.SonTarih).format('DD.MM.YYYY') : '',
        actions: (
          <div className="d-flex gap-2">
            <IPButton text="Detay" className="btn btn-sm btn-primary" onClick={() => openDealerModal(d)} />
          </div>
        ),
      })),
    [returnDealerList]
  )

  // -------------------------
  // Reason Summary Table (+ Detay)
  // -------------------------
  const reasonSummaryColumns = useMemo(
    () => [
      { Header: 'İade Nedeni', accessor: 'reason' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
      { Header: 'İlk Tarih', accessor: 'IlkTarih' },
      { Header: 'Son Tarih', accessor: 'SonTarih' },
      { Header: 'Detay', accessor: 'actions' },
    ],
    []
  )

  const reasonSummaryData = useMemo(
    () =>
      (returnReasonList || []).map((r) => ({
        reason: r.IadeNedeniAdi,
        KayitSayisi: r.KayitSayisi,
        ToplamMiktar: r.ToplamMiktar,
        Onayli: r.Onayli,
        Reddedilen: r.Reddedilen,
        IslemGormemis: r.IslemGormemis,
        Silinen: r.Silinen,
        Iptal: r.Iptal,
        Iade: r.Iade,
        IlkTarih: r.IlkTarih ? moment(r.IlkTarih).format('DD.MM.YYYY') : '',
        SonTarih: r.SonTarih ? moment(r.SonTarih).format('DD.MM.YYYY') : '',
        actions: (
          <div className="d-flex gap-2">
            <IPButton text="Detay" className="btn btn-sm btn-primary" onClick={() => openReasonModal(r)} />
          </div>
        ),
      })),
    [returnReasonList]
  )

  // ============================================================
  // MODAL TABLES (MT)
  // ============================================================
  const mtProductColumns = useMemo(
    () => [
      { Header: 'Ürün Adı', accessor: 'UrunAdi' },
      { Header: 'Ürün Kodu', accessor: 'UrunKodu' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
    ],
    []
  )

  const mtReasonColumns = useMemo(
    () => [
      { Header: 'İade Nedeni', accessor: 'IadeNedeniAdi' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
      { Header: 'Ürün Kırılımı', accessor: 'actions' },
    ],
    []
  )

  const mtReasonProductColumns = useMemo(
    () => [
      { Header: 'Ürün Adı', accessor: 'UrunAdi' },
      { Header: 'Ürün Kodu', accessor: 'UrunKodu' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
    ],
    []
  )

  const selectedMTReason = useMemo(() => {
    if (!selectedMT || selectedMTReasonId == null) return null
    return (selectedMT as any).IadeNedenleri?.find((x: any) => x.IadeNedeniId === selectedMTReasonId) ?? null
  }, [selectedMT, selectedMTReasonId])

  const mtProductData = useMemo(() => {
    if (!selectedMT) return []
    const list = (selectedMT as any).Urunler || []
    return list.map((item: any) => ({
      ...item,
      UrunKodu: (
        <Link target="_blank" to={`/product/detail/${item.UrunId}?from=productList`}>
          {item.UrunKodu}
        </Link>
      ),
    }))
  }, [selectedMT])

  const mtReasonData = useMemo(() => {
    if (!selectedMT) return []
    const list = (selectedMT as any).IadeNedenleri || []
    return list.map((r: any) => ({
      ...r,
      actions: (
        <div className="d-flex gap-2">
          <IPButton
            text={selectedMTReasonId === r.IadeNedeniId ? 'Seçili' : 'Seç'}
            className={`btn btn-sm ${selectedMTReasonId === r.IadeNedeniId ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => setSelectedMTReasonId(r.IadeNedeniId)}
          />
        </div>
      ),
    }))
  }, [selectedMT, selectedMTReasonId])

  const mtReasonProductData = useMemo(() => {
    if (!selectedMTReason) return []
    const list = (selectedMTReason as any).Urunler || []
    return list.map((item: any) => ({
      ...item,
      UrunKodu: (
        <Link target="_blank" to={`/product/detail/${item.UrunId}?from=productList`}>
          {item.UrunKodu}
        </Link>
      ),
    }))
  }, [selectedMTReason])

  // ============================================================
  // MODAL TABLES (PRODUCT)
  // ============================================================
  const productUserColumns = useMemo(
    () => [
      { Header: 'Personel', accessor: 'mt' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
    ],
    []
  )

  const productReasonColumns = useMemo(
    () => [
      { Header: 'İade Nedeni', accessor: 'IadeNedeniAdi' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
      { Header: 'Kullanıcı Kırılımı', accessor: 'actions' },
    ],
    []
  )

  const productReasonUserColumns = useMemo(
    () => [
      { Header: 'Personel', accessor: 'mt' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
    ],
    []
  )

  const selectedProductReason = useMemo(() => {
    if (!selectedProduct || selectedProductReasonId == null) return null
    return selectedProduct.IadeNedenleri?.find((x) => x.IadeNedeniId === selectedProductReasonId) ?? null
  }, [selectedProduct, selectedProductReasonId])

  const productUserData = useMemo(() => {
    if (!selectedProduct) return []
    return (selectedProduct.Kullanicilar || []).map((u: any) => ({
      mt: `${u.Name} ${u.Surname} (${u.UserName})`,
      KayitSayisi: u.KayitSayisi,
      ToplamMiktar: u.ToplamMiktar,
      Onayli: u.Onayli,
      Reddedilen: u.Reddedilen,
      IslemGormemis: u.IslemGormemis,
      Silinen: u.Silinen,
      Iptal: u.Iptal,
      Iade: u.Iade,
    }))
  }, [selectedProduct])

  const productReasonData = useMemo(() => {
    if (!selectedProduct) return []
    return (selectedProduct.IadeNedenleri || []).map((r: any) => ({
      ...r,
      actions: (
        <div className="d-flex gap-2">
          <IPButton
            text={selectedProductReasonId === r.IadeNedeniId ? 'Seçili' : 'Seç'}
            className={`btn btn-sm ${selectedProductReasonId === r.IadeNedeniId ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => setSelectedProductReasonId(r.IadeNedeniId)}
          />
        </div>
      ),
    }))
  }, [selectedProduct, selectedProductReasonId])

  const productReasonUserData = useMemo(() => {
    if (!selectedProductReason) return []
    return (selectedProductReason.Kullanicilar || []).map((u: any) => ({
      mt: `${u.Name} ${u.Surname} (${u.UserName})`,
      KayitSayisi: u.KayitSayisi,
      ToplamMiktar: u.ToplamMiktar,
      Onayli: u.Onayli,
      Reddedilen: u.Reddedilen,
      IslemGormemis: u.IslemGormemis,
      Silinen: u.Silinen,
      Iptal: u.Iptal,
      Iade: u.Iade,
    }))
  }, [selectedProductReason])

  // ============================================================
  // MODAL TABLES (DEALER)
  // ============================================================
  const dealerUserColumns = productUserColumns
  const dealerProductColumns = useMemo(
    () => [
      { Header: 'Ürün Adı', accessor: 'UrunAdi' },
      { Header: 'Ürün Kodu', accessor: 'UrunKodu' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
    ],
    []
  )

  const dealerReasonColumns = useMemo(
    () => [
      { Header: 'İade Nedeni', accessor: 'IadeNedeniAdi' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
      { Header: 'Ürün Kırılımı', accessor: 'actions' },
    ],
    []
  )

  const dealerReasonProductColumns = dealerProductColumns

  const selectedDealerReason = useMemo(() => {
    if (!selectedDealer || selectedDealerReasonId == null) return null
    return selectedDealer.IadeNedenleri?.find((x: any) => x.IadeNedeniId === selectedDealerReasonId) ?? null
  }, [selectedDealer, selectedDealerReasonId])

  const dealerUserData = useMemo(() => {
    if (!selectedDealer) return []
    return (selectedDealer.Kullanicilar || []).map((u: any) => ({
      mt: `${u.Name} ${u.Surname} (${u.UserName})`,
      KayitSayisi: u.KayitSayisi,
      ToplamMiktar: u.ToplamMiktar,
      Onayli: u.Onayli,
      Reddedilen: u.Reddedilen,
      IslemGormemis: u.IslemGormemis,
      Silinen: u.Silinen,
      Iptal: u.Iptal,
      Iade: u.Iade,
    }))
  }, [selectedDealer])

  const dealerProductData = useMemo(() => {
    if (!selectedDealer) return []
    return (selectedDealer.Urunler || []).map((p: any) => ({
      ...p,
      UrunKodu: (
        <Link target="_blank" to={`/product/detail/${p.UrunId}?from=productList`}>
          {p.UrunKodu}
        </Link>
      ),
    }))
  }, [selectedDealer])

  const dealerReasonData = useMemo(() => {
    if (!selectedDealer) return []
    return (selectedDealer.IadeNedenleri || []).map((r: any) => ({
      ...r,
      actions: (
        <div className="d-flex gap-2">
          <IPButton
            text={selectedDealerReasonId === r.IadeNedeniId ? 'Seçili' : 'Seç'}
            className={`btn btn-sm ${selectedDealerReasonId === r.IadeNedeniId ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => setSelectedDealerReasonId(r.IadeNedeniId)}
          />
        </div>
      ),
    }))
  }, [selectedDealer, selectedDealerReasonId])

  const dealerReasonProductData = useMemo(() => {
    if (!selectedDealerReason) return []
    return (selectedDealerReason as any).Urunler?.map((p: any) => ({
      ...p,
      UrunKodu: (
        <Link target="_blank" to={`/product/detail/${p.UrunId}?from=productList`}>
          {p.UrunKodu}
        </Link>
      ),
    })) || []
  }, [selectedDealerReason])

  // ============================================================
  // MODAL TABLES (REASON)
  // ============================================================
  const reasonModalProductColumns = dealerProductColumns
  const reasonModalDealerColumns = useMemo(
    () => [
      { Header: 'Bayi', accessor: 'dealer' },
      { Header: 'Kayıt', accessor: 'KayitSayisi' },
      { Header: 'Toplam', accessor: 'ToplamMiktar' },
      { Header: 'Onaylı', accessor: 'Onayli' },
      { Header: 'Reddedilen', accessor: 'Reddedilen' },
      { Header: 'İşlem Görmemiş', accessor: 'IslemGormemis' },
      { Header: 'Silinen', accessor: 'Silinen' },
      { Header: 'İptal', accessor: 'Iptal' },
      { Header: 'İade', accessor: 'Iade' },
    ],
    []
  )

  const reasonModalProductData = useMemo(() => {
    if (!selectedReasonSummary) return []
    return (selectedReasonSummary.Urunler || []).map((p: any) => ({
      ...p,
      UrunKodu: (
        <Link target="_blank" to={`/product/detail/${p.UrunId}?from=productList`}>
          {p.UrunKodu}
        </Link>
      ),
    }))
  }, [selectedReasonSummary])

  const reasonModalDealerData = useMemo(() => {
    if (!selectedReasonSummary) return []
    return (selectedReasonSummary.Bayiler || []).map((d: any) => ({
      dealer: d.DealerName ? `${d.DealerCode} - ${d.DealerName}` : d.DealerCode,
      KayitSayisi: d.KayitSayisi,
      ToplamMiktar: d.ToplamMiktar,
      Onayli: d.Onayli,
      Reddedilen: d.Reddedilen,
      IslemGormemis: d.IslemGormemis,
      Silinen: d.Silinen,
      Iptal: d.Iptal,
      Iade: d.Iade,
    }))
  }, [selectedReasonSummary])

  return (
    <div className="">
      <div className="content col-lg-12 m-auto">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="col-lg-10 d-flex align-items-center">
              <h4>İptal / İade Raporu</h4>
            </div>
            <div className="col-lg-2 d-flex justify-content-end gap-2">
              <IPButton text="Göster" className="btn btn-danger" onClick={() => refreshAll()} />
            </div>
          </div>

          {/* Filters */}
          <div className="row ml-2 mr-2">
            <div className="col-lg-2 mt-5">
              <InputCampaign
                type="date"
                style={{ width: '100%' }}
                id="StartDate"
                name="StartDate"
                label={'Başlangıç Tarihi'}
                value={toDateTimeLocal(startDate)}
                onChange={(e) => getDate(e.target.value, 'Start')}
                isActive
              />
            </div>

            <div className="col-lg-2 mt-5">
              <InputCampaign
                style={{ width: '100%' }}
                type="date"
                id="EndDate"
                name="EndDate"
                label={'Bitiş Tarihi'}
                value={toDateTimeLocal(endDate)}
                onChange={(e) => getDate(e.target.value, 'End')}
                isActive
              />
            </div>

            <div className="col-lg-2 mt-3">
              <label htmlFor="company" className="form-label">
                Şirket
              </label>
              <IPSelectBox
                isClearable
                id="Şirket"
                name="Şirket"
                options={state.companies}
                value={state.selectedCompany}
                onChangeSingle={(option) => setState({ selectedCompany: option })}
              />
            </div>

            <div className="col-lg-2 mt-3">
              <label htmlFor="dealer" className="form-label">
                Bayi Kodu
              </label>
              <IPInput
                type="text"
                className="form-control"
                name="Dealer"
                placeholder="Bayi Kodu"
                onChange={(e) => setState({ dealerCode: String(e.target.value).trim() })}
                value={state.dealerCode}
              />
            </div>

            <div className="col-lg-2 mt-3">
              <label htmlFor="type" className="form-label">
                İşlem Tipi
              </label>
              <IPSelectBox
                isClearable
                placeholder="İşlem Tipi"
                options={types}
                value={state.selectedType}
                onChangeSingle={(option) => setState({ selectedType: option })}
              />
            </div>
          </div>

          <div className="row ml-2 mr-2">
            <div className="col-lg-2 mt-3">
              <label htmlFor="product" className="form-label">
                Ürün Kodu
              </label>
              <IPInput
                type="text"
                className="form-control"
                name="Product"
                placeholder="Ürün Kodu"
                onChange={(e) => setState({ productCode: String(e.target.value).trim() })}
                value={state.productCode}
              />
            </div>

            <div className="col-lg-2 mt-3">
              <label htmlFor="brand" className="form-label">
                Marka
              </label>
              <IPSelectBox
                isClearable
                id="Brand"
                name="Brand"
                placeholder="Marka"
                options={state.brandList?.map((brand) => ({ label: brand.Name, value: brand.Id })) || []}
                value={state.brand as unknown as ISelectOption}
                onChangeSingle={(option) => setState({ brand: option })}
              />
            </div>

            <div className="col-lg-2 mt-3">
              <label htmlFor="personel" className="form-label">
                Personel
              </label>
              <IPSelectBox
                isClearable
                id="AppUser"
                name="AppUser"
                placeholder="Personel"
                options={state.personelList?.map((item: any) => ({ label: item.Name, value: item.Id })) || []}
                value={state.personel as unknown as ISelectOption}
                onChangeSingle={(option) => setState({ personel: option })}
              />
            </div>

            <div className="col-lg-2 mt-3">
              <label htmlFor="reason" className="form-label">
                İade Nedeni
              </label>
              <IPSelectBox
                isClearable
                id="Reason"
                name="Reason"
                placeholder="İade Nedeni"
                options={iadeNedenleri}
                value={state.reason}
                onChangeSingle={(option) => setState({ reason: option })}
              />
            </div>

            <div className="col-lg-2 mt-3">
              <label htmlFor="status" className="form-label">
                Durum
              </label>
              <IPSelectBox
                isClearable
                placeholder="Durum"
                options={status}
                value={state.selectedStatus}
                onChangeSingle={(option) => setState({ selectedStatus: option })}
              />
            </div>
          </div>

          {/* Tables */}
          <div className="card-body">
            <div className="row">
              <div className="col-lg-12">
                {/* Detail */}
                <div className="row">
                  <div className="col-sm-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="m-0">Kayıt Detayları</h5>
                    </div>
                    <Table columns={detailColumns} data={detailData} currentPage={0} />
                  </div>
                </div>

                {/* MT Summary */}
                <div className="row mb-4">
                  <div className="col-sm-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="m-0">Personel Bazlı Özet</h5>
                    </div>
                    <Table columns={mtSummaryColumns} data={mtSummaryData} currentPage={0} />
                  </div>
                </div>

                {/* Product Summary */}
                <div className="row mb-4">
                  <div className="col-sm-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="m-0">Ürün Bazlı Özet</h5>
                    </div>
                    <Table columns={productSummaryColumns} data={productSummaryData} currentPage={0} />
                  </div>
                </div>

                {/* Dealer Summary */}
                <div className="row mb-4">
                  <div className="col-sm-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="m-0">Bayi Bazlı Özet</h5>
                    </div>
                    <Table columns={dealerSummaryColumns} data={dealerSummaryData} currentPage={0} />
                  </div>
                </div>

                {/* Reason Summary */}
                <div className="row mb-4">
                  <div className="col-sm-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="m-0">İade Nedeni Bazlı Özet</h5>
                    </div>
                    <Table columns={reasonSummaryColumns} data={reasonSummaryData} currentPage={0} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================
            MT MODAL
        ============================================================ */}
        <IPModal
          show={isMTModalOpen}
          onClose={() => {
            setIsMTModalOpen(false)
            setSelectedMT(null)
            setSelectedMTReasonId(null)
          }}
          title={
            selectedMT ? `Personel Detayı - ${selectedMT.Name} ${selectedMT.Surname} (${selectedMT.UserName})` : 'Personel Detayı'
          }
          width={1400}
          bodyOverflow={true}
          hideConfirm={true}
        >
          <div className="p-2">
            {selectedMT && (
              <>
                <div className="row mb-3">
                  <div className="col-lg-12">
                    <div className="d-flex flex-wrap gap-3">
                      <div>
                        <b>Kayıt:</b> {selectedMT.KayitSayisi}
                      </div>
                      <div>
                        <b>Toplam:</b> {selectedMT.ToplamMiktar}
                      </div>
                      <div>
                        <b>Onaylı:</b> {selectedMT.Onayli}
                      </div>
                      <div>
                        <b>Reddedilen:</b> {selectedMT.Reddedilen}
                      </div>
                      <div>
                        <b>Silinen:</b> {selectedMT.Silinen}
                      </div>
                      <div>
                        <b>İptal:</b> {selectedMT.Iptal}
                      </div>
                      <div>
                        <b>İade:</b> {selectedMT.Iade}
                      </div>
                      <div>
                        <b>İlk Tarih:</b> {selectedMT.IlkTarih ? moment(selectedMT.IlkTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                      <div>
                        <b>Son Tarih:</b> {selectedMT.SonTarih ? moment(selectedMT.SonTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">Ürün Özeti</h6>
                    <Table columns={mtProductColumns} data={mtProductData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">İade Nedenleri (satırdan “Seç” ile ürün kırılımını görüntüle)</h6>
                    <Table columns={mtReasonColumns} data={mtReasonData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <h6 className="mb-2">Seçili İade Nedeni Ürünleri {selectedMTReason ? `- ${selectedMTReason.IadeNedeniAdi}` : ''}</h6>
                    <Table columns={mtReasonProductColumns} data={mtReasonProductData} currentPage={0} />
                  </div>
                </div>
              </>
            )}

            {!selectedMT && <div className="text-center p-4">Kayıt seçilmedi.</div>}
          </div>
        </IPModal>

        {/* ============================================================
            PRODUCT MODAL
        ============================================================ */}
        <IPModal
          show={isProductModalOpen}
          onClose={() => {
            setIsProductModalOpen(false)
            setSelectedProduct(null)
            setSelectedProductReasonId(null)
          }}
          title={
            selectedProduct
              ? `Ürün Detayı - ${selectedProduct.UrunKodu} - ${selectedProduct.UrunAdi}`
              : 'Ürün Detayı'
          }
          width={1400}
          bodyOverflow={true}
          hideConfirm={true}
        >
          <div className="p-2">
            {selectedProduct && (
              <>
                <div className="row mb-3">
                  <div className="col-lg-12">
                    <div className="d-flex flex-wrap gap-3">
                      <div>
                        <b>Kayıt:</b> {selectedProduct.KayitSayisi}
                      </div>
                      <div>
                        <b>Toplam:</b> {selectedProduct.ToplamMiktar}
                      </div>
                      <div>
                        <b>Onaylı:</b> {selectedProduct.Onayli}
                      </div>
                      <div>
                        <b>Reddedilen:</b> {selectedProduct.Reddedilen}
                      </div>
                      <div>
                        <b>Silinen:</b> {selectedProduct.Silinen}
                      </div>
                      <div>
                        <b>İptal:</b> {selectedProduct.Iptal}
                      </div>
                      <div>
                        <b>İade:</b> {selectedProduct.Iade}
                      </div>
                      <div>
                        <b>İlk Tarih:</b> {selectedProduct.IlkTarih ? moment(selectedProduct.IlkTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                      <div>
                        <b>Son Tarih:</b> {selectedProduct.SonTarih ? moment(selectedProduct.SonTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">Kullanıcı Özeti</h6>
                    <Table columns={productUserColumns} data={productUserData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">İade Nedenleri (satırdan “Seç” ile kullanıcı kırılımını görüntüle)</h6>
                    <Table columns={productReasonColumns} data={productReasonData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <h6 className="mb-2">
                      Seçili İade Nedeni Kullanıcıları {selectedProductReason ? `- ${selectedProductReason.IadeNedeniAdi}` : ''}
                    </h6>
                    <Table columns={productReasonUserColumns} data={productReasonUserData} currentPage={0} />
                  </div>
                </div>
              </>
            )}

            {!selectedProduct && <div className="text-center p-4">Kayıt seçilmedi.</div>}
          </div>
        </IPModal>

        {/* ============================================================
            DEALER MODAL
        ============================================================ */}
        <IPModal
          show={isDealerModalOpen}
          onClose={() => {
            setIsDealerModalOpen(false)
            setSelectedDealer(null)
            setSelectedDealerReasonId(null)
          }}
          title={
            selectedDealer
              ? `Bayi Detayı - ${selectedDealer.DealerName ? `${selectedDealer.DealerCode} - ${selectedDealer.DealerName}` : selectedDealer.DealerCode}`
              : 'Bayi Detayı'
          }
          width={1400}
          bodyOverflow={true}
          hideConfirm={true}
        >
          <div className="p-2">
            {selectedDealer && (
              <>
                <div className="row mb-3">
                  <div className="col-lg-12">
                    <div className="d-flex flex-wrap gap-3">
                      <div>
                        <b>Kayıt:</b> {selectedDealer.KayitSayisi}
                      </div>
                      <div>
                        <b>Toplam:</b> {selectedDealer.ToplamMiktar}
                      </div>
                      <div>
                        <b>Onaylı:</b> {selectedDealer.Onayli}
                      </div>
                      <div>
                        <b>Reddedilen:</b> {selectedDealer.Reddedilen}
                      </div>
                      <div>
                        <b>Silinen:</b> {selectedDealer.Silinen}
                      </div>
                      <div>
                        <b>İptal:</b> {selectedDealer.Iptal}
                      </div>
                      <div>
                        <b>İade:</b> {selectedDealer.Iade}
                      </div>
                      <div>
                        <b>İlk Tarih:</b> {selectedDealer.IlkTarih ? moment(selectedDealer.IlkTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                      <div>
                        <b>Son Tarih:</b> {selectedDealer.SonTarih ? moment(selectedDealer.SonTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">Kullanıcı Özeti</h6>
                    <Table columns={dealerUserColumns} data={dealerUserData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">Ürün Özeti</h6>
                    <Table columns={dealerProductColumns} data={dealerProductData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">İade Nedenleri (satırdan “Seç” ile ürün kırılımını görüntüle)</h6>
                    <Table columns={dealerReasonColumns} data={dealerReasonData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <h6 className="mb-2">
                      Seçili İade Nedeni Ürünleri {selectedDealerReason ? `- ${selectedDealerReason.IadeNedeniAdi}` : ''}
                    </h6>
                    <Table columns={dealerReasonProductColumns} data={dealerReasonProductData} currentPage={0} />
                  </div>
                </div>
              </>
            )}

            {!selectedDealer && <div className="text-center p-4">Kayıt seçilmedi.</div>}
          </div>
        </IPModal>

        {/* ============================================================
            REASON MODAL
        ============================================================ */}
        <IPModal
          show={isReasonModalOpen}
          onClose={() => {
            setIsReasonModalOpen(false)
            setSelectedReasonSummary(null)
          }}
          title={selectedReasonSummary ? `İade Nedeni Detayı - ${selectedReasonSummary.IadeNedeniAdi}` : 'İade Nedeni Detayı'}
          width={1400}
          bodyOverflow={true}
          hideConfirm={true}
        >
          <div className="p-2">
            {selectedReasonSummary && (
              <>
                <div className="row mb-3">
                  <div className="col-lg-12">
                    <div className="d-flex flex-wrap gap-3">
                      <div>
                        <b>Kayıt:</b> {selectedReasonSummary.KayitSayisi}
                      </div>
                      <div>
                        <b>Toplam:</b> {selectedReasonSummary.ToplamMiktar}
                      </div>
                      <div>
                        <b>Onaylı:</b> {selectedReasonSummary.Onayli}
                      </div>
                      <div>
                        <b>Reddedilen:</b> {selectedReasonSummary.Reddedilen}
                      </div>
                      <div>
                        <b>Silinen:</b> {selectedReasonSummary.Silinen}
                      </div>
                      <div>
                        <b>İptal:</b> {selectedReasonSummary.Iptal}
                      </div>
                      <div>
                        <b>İade:</b> {selectedReasonSummary.Iade}
                      </div>
                      <div>
                        <b>İlk Tarih:</b> {selectedReasonSummary.IlkTarih ? moment(selectedReasonSummary.IlkTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                      <div>
                        <b>Son Tarih:</b> {selectedReasonSummary.SonTarih ? moment(selectedReasonSummary.SonTarih).format('DD.MM.YYYY') : '-'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <h6 className="mb-2">Ürün Kırılımı</h6>
                    <Table columns={reasonModalProductColumns} data={reasonModalProductData} currentPage={0} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <h6 className="mb-2">Bayi Kırılımı</h6>
                    <Table columns={reasonModalDealerColumns} data={reasonModalDealerData} currentPage={0} />
                  </div>
                </div>
              </>
            )}

            {!selectedReasonSummary && <div className="text-center p-4">Kayıt seçilmedi.</div>}
          </div>
        </IPModal>
      </div>
    </div>
  )
}

export default componentWithContext(Index)
